import React from "react";
import { Lock } from "lucide-react";

import Ring from "./charts/Ring";
import { Empty, Kpi, Section, TableScroll, Td, Th } from "./primitives";
import { NOT_RECORDED, STATE_COLORS, fmtNumber, fmtPercent, fmtPeriod, fmtShortDate, stateForScore } from "./reportFormat";

/**
 * Sections the user-facing reports add on top of the shared athlete sections
 * (athleteSections.jsx): the CEO mockups' header, the athlete's state with its
 * action cards, the session log, meal habits (no calories), the rule-based
 * advice, and the "locked" card that replaces data the viewer may not see.
 */

const Chip = ({ children, color }) => (
  <span
    className="rounded-full border px-3 py-1 text-[12.5px]"
    style={
      color
        ? { background: color, borderColor: color, color: "#fff" }
        : { background: "#16336d", borderColor: "#2351ac", color: "#d7dff2" }
    }
  >
    {children}
  </span>
);

export const ReportHeader = ({ eyebrow, title, subtitle, chips = [], meta = [], notice }) => (
  <Section tone="dark">
    <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
      <div className="min-w-0">
        <p className="text-[11px] tracking-[0.14em] text-peri-200 uppercase">{eyebrow}</p>
        <h1 className="mt-1 font-display text-[40px] leading-none font-semibold text-white sm:text-[46px]">{title}</h1>
        {subtitle && <p className="mt-1.5 text-sm text-brand-200">{subtitle}</p>}
        {chips.length > 0 && (
          <div className="mt-3.5 flex flex-wrap gap-2">
            {chips.map((chip) => (
              <Chip key={chip.label} color={chip.color}>
                {chip.label}
              </Chip>
            ))}
          </div>
        )}
      </div>
      {meta.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] content-start gap-x-3.5 gap-y-1.5 text-[13px]">
          {meta.map(([term, value]) => (
            <React.Fragment key={term}>
              <dt className="text-peri-200">{term}</dt>
              <dd className="m-0 text-right">{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
    {notice && <p className="mt-4 border-t border-navy-700 pt-3 text-[12.5px] leading-relaxed text-peri-200">{notice}</p>}
  </Section>
);

/** Profile facts shared by every athlete header. */
export const athleteMeta = (report) => {
  const a = report.athlete;
  return [
    ["Edad", a.age === null ? NOT_RECORDED : `${a.age} años`],
    ["Peso / altura", `${a.weight ? `${a.weight} kg` : NOT_RECORDED} · ${a.height ? `${a.height} cm` : NOT_RECORDED}`],
    ["Club", a.club || NOT_RECORDED],
    ["Periodo", fmtPeriod(report.period)],
  ];
};

export const lastCheckinLine = (checkin) => {
  if (!checkin) return null;
  const parts = [
    checkin.sleep_hours !== null &&
      `sueño ${fmtNumber(checkin.sleep_hours, 1)} h${checkin.sleep_quality !== null ? ` (calidad ${checkin.sleep_quality}/5)` : ""}`,
    checkin.rpe !== null && `RPE ${checkin.rpe}/10`,
    checkin.fatigue !== null && `fatiga ${checkin.fatigue}/10`,
    checkin.doms !== null && `dolor ${checkin.doms}/10`,
    checkin.mood !== null && `ánimo ${checkin.mood}/10`,
    checkin.motivation !== null && `motivación ${checkin.motivation}/10`,
  ].filter(Boolean);
  return `Último check-in: ${fmtShortDate(checkin.date)} · ${parts.join(" · ")}`;
};

/** "Mi estado" / "Estado": ring, tier message, last check-in, action cards. */
export const StateCardSection = ({ report, title, stateCopy }) => {
  const a = report.athlete;
  const color = a.state?.color ?? STATE_COLORS.missing;
  const line = lastCheckinLine(report.last_checkin);

  return (
    <Section title={title} meta="Promedio de los últimos 3 check-ins">
      {a.score === null ? (
        <Empty>Sin check-ins en el periodo. El estado se calcula desde el check-in diario de la app.</Empty>
      ) : (
        <>
          <div className="grid items-center gap-6 sm:grid-cols-[200px_1fr]">
            <div className="mx-auto w-[180px] sm:w-full">
              <Ring value={a.score} color={color} caption={a.state?.name} />
            </div>
            <div>
              <p className="font-display text-[28px] font-semibold" style={{ color }}>
                {a.state?.name}
              </p>
              {stateCopy?.message && <p className="mt-1 max-w-[62ch] text-[15px] text-navy-900">{stateCopy.message}</p>}
              {line && <p className="mt-3 text-[12.5px] text-ink">{line}</p>}
              {a.checkin_adherence !== null && (
                <p className="mt-1 text-[12.5px] text-ink">
                  Adherencia al check-in: {fmtPercent(a.checkin_adherence)} de los {report.period.days} días
                </p>
              )}
            </div>
          </div>
          {stateCopy?.actions?.length > 0 && (
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              {stateCopy.actions.map((action) => (
                <div key={action.title} className="rounded-lg border-l-4 bg-paper px-4 py-3" style={{ borderLeftColor: color }}>
                  <p className="text-sm font-semibold text-navy-900">{action.title}</p>
                  <p className="mt-0.5 text-[13px] text-ink">{action.detail}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Section>
  );
};

/** "Historial de sesiones": one row per day with session metrics. */
export const SessionHistorySection = ({ report, withState }) => {
  const metrics = report.athlete.metrics.slice(0, 7);
  const rows = report.session_history;

  return (
    <Section title="Historial de sesiones" meta="De la más reciente a la más antigua">
      {rows.length === 0 ? (
        <Empty>Sin sesiones registradas en el periodo.</Empty>
      ) : (
        <TableScroll minWidth={Math.max(560, 110 + metrics.length * 100)}>
          <table className="w-full border-collapse text-[13.5px]">
            <thead>
              <tr>
                <Th>Fecha</Th>
                {metrics.map((m) => (
                  <Th key={m.key} align="right">
                    {m.title}
                    {m.unit ? ` (${m.unit})` : ""}
                  </Th>
                ))}
                {withState && <Th>Estado ese día</Th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((day) => (
                <tr key={day.date}>
                  <Td>{fmtShortDate(day.date)}</Td>
                  {metrics.map((m) => (
                    <Td key={m.key} align="right">
                      {fmtNumber(day.values[m.key] ?? null, m.unit === "%" ? 0 : 1)}
                    </Td>
                  ))}
                  {withState && (
                    <Td>
                      {day.score === null ? (
                        <span className="text-peri-300">{NOT_RECORDED}</span>
                      ) : (
                        <span
                          className="rounded-full px-2 py-0.5 text-[12px] font-semibold"
                          style={{ color: stateForScore(day.score).color, background: `${stateForScore(day.score).color}22` }}
                        >
                          {Math.round(day.score)}%
                        </span>
                      )}
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </Section>
  );
};

/** "Mi alimentación": meal habit, never calories — the mockup is explicit. */
export const MealHabitSection = ({ report, title }) => {
  const n = report.athlete.nutrition;
  if (!n) return null;
  const tone = (count, bad) => (count >= Math.ceil(n.window_days * 0.7) ? STATE_COLORS.ok : bad);
  return (
    <Section title={title} meta={`Comidas registradas en los últimos ${n.window_days} días · hábito, no calorías`}>
      <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        <Kpi label="Días completos" value={n.complete_days} unit={`/${n.window_days}`} note="Desayuno, almuerzo y cena" color={tone(n.complete_days, STATE_COLORS.danger)} />
        <Kpi label="Desayunos" value={n.breakfast_days} unit={`/${n.window_days}`} note="Más ligado al sprint final" color={tone(n.breakfast_days, STATE_COLORS.danger)} />
        <Kpi label="Almuerzos" value={n.lunch_days} unit={`/${n.window_days}`} note="Registrados" color={tone(n.lunch_days, "#4274d9")} />
        <Kpi label="Cenas" value={n.dinner_days} unit={`/${n.window_days}`} note="Registradas" color={tone(n.dinner_days, "#4274d9")} />
      </div>
      <p className="mt-3 text-[12px] text-peri-300">
        Athletain no fija metas de calorías ni de peso. Si tienes dudas, habla con el nutricionista del club.
      </p>
    </Section>
  );
};

export const AdviceSection = ({ title, meta, items }) => (
  <Section title={title} meta={meta}>
    {items.length === 0 ? (
      <Empty>Sin acciones prioritarias en este periodo.</Empty>
    ) : (
      <ol className="space-y-3">
        {items.map((item, index) => (
          <li key={item.title} className="flex gap-3 text-sm text-ink">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-navy-900 text-[12px] font-bold text-white">
              {index + 1}
            </span>
            <span>
              <b className="text-navy-900">{item.title}:</b> {item.detail}
            </span>
          </li>
        ))}
      </ol>
    )}
  </Section>
);

export const LockedSection = ({ title, text, items }) => (
  <Section title={title}>
    <div className="rounded-lg border border-dashed border-peri-200 p-4">
      <div className="grid gap-2.5 sm:grid-cols-3">
        {items.map((item) => (
          <div key={item} className="flex items-center gap-2 rounded-md bg-paper px-3 py-2.5 text-[13px] text-ink">
            <Lock size={14} className="shrink-0 text-peri-300" aria-hidden="true" />
            {item}
          </div>
        ))}
      </div>
      <p className="mt-3 text-[12.5px] leading-relaxed text-ink">{text}</p>
    </div>
  </Section>
);

export const LegalFooter = () => (
  <p className="mx-auto max-w-[1080px] px-5 pb-10 text-[11.5px] leading-relaxed text-peri-300">
    Los indicadores de bienestar y nutrición son autorreportados y no constituyen diagnóstico médico. Athletain
    S.A.S. · NIT 901.939.261-5 · Cali, Colombia.
  </p>
);
